import { Component, Vue } from 'vue-property-decorator';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import axios from 'axios';
import { taxonomyAPI } from '@/providers/apis/taxonomy/taxonomy';
import { SubjectModel } from '@/models/taxonomy/subject';
import { ClassificationModel } from '@/models/taxonomy/classification';
import { sessionService } from '@/providers/services/auth/session';
import { FACETS_DEFAULT_FILTER } from '@/utils/constants';
import { sortByProperty } from '@/utils/utils';

@Component({
  name: 'taxonomy-filter',
  components: {
    'material-icon': GoogleMaterialIcon,
  },
  filters: {
    extractClassification(subjectCode: string) {
      return subjectCode ? subjectCode.split('.')[0] : '';
    },
  },
})

export default class TaxonomyFilter extends Vue {

  private get isSuperUser() {
    return this.$access.hasPermission('all', 'all');
  }


  public userId: string = '';

  public selectedSubjects: SubjectModel[] = [];

  public mindsetSubject: any = ['K12.SEL'];

  // --------------------------------------------------------------
  // Properties

  private isShowFilterBody: boolean = false;

  private defaultClassificationIds: [string, string] | any = ['k_12', 'skills_training'];

  private classifications: ClassificationModel[] = [];

  private subjects: SubjectModel[] = [];

  private activeClassification!: ClassificationModel;

  private defaultOrderOfFacets: any = [
    'K12.MA',
    'K12.SC',
    'K12.ELA',
    'K12.SS',
    'SK.HOS',
    'SK.XAPI',
  ];

  private tenanSubject: any = [
    'K12.SS',
    'K12.MA',
    'K12.ELA',
    'K12.SC',
  ];

  // ---------------------------------------------------
  // This is for the demo purpose
  private topangAccountDefault: any = [
    'SK.SKS',
    'SK.SEL',
  ];

  private hask12Enabled: boolean = false;

  // ----------------------------------------------------------------
  // Hooks

  public mounted() {
      if ( this.$route && this.$route.params.id) {
        this.userId =  this.$route.params.id;
      }
      if (FACETS_DEFAULT_FILTER.indexOf(this.userId) !== -1) {
          this.defaultOrderOfFacets = this.defaultOrderOfFacets.concat(this.topangAccountDefault);
      }
      this.loadTaxonomyData();
  }


  public loadFilterOutside(userId: string) {
      this.userId = userId;
      if (FACETS_DEFAULT_FILTER.indexOf(this.userId) !== -1) {
        this.topangAccountDefault = [...this.topangAccountDefault, ...this.mindsetSubject];
        this.defaultOrderOfFacets = this.defaultOrderOfFacets.concat(this.topangAccountDefault);
    }
      this.loadTaxonomyData();
  }


  // ------------------------------------------------------------------
  // Methods

  public loadTaxonomyData() {
    const component = this;
    taxonomyAPI.fetchTaxonomyClassifications().then((taxonomyClassifications: ClassificationModel[]) =>  {
      component.classifications = taxonomyClassifications;
      if (!this.isSuperUser) {
        const activeCategory = taxonomyClassifications.filter((item) => item.default).map((item) => item.id);
        this.defaultClassificationIds = activeCategory.length
                ? activeCategory
                : taxonomyClassifications.filter(
                    (item, index) => index <= 1 || item.id === 'k_12').map((item) => item.id);
        if (this.defaultClassificationIds.indexOf('k_12') !== -1) {
          this.hask12Enabled = true;
        }
      }
      const promiseData = component.defaultClassificationIds.map((defaultClassificationId: string) => {
        return new Promise((resolve) => {
          let defaultClassification: ClassificationModel | any = taxonomyClassifications.find(
            (classification: ClassificationModel) => classification.id === defaultClassificationId,
          );
          defaultClassification = defaultClassification || taxonomyClassifications[0];
          component.loadTaxonomySubjects(defaultClassification.id).then((taxonomySubjects: SubjectModel[]) => {
            if (!this.isSuperUser) {
              const activeFactsOrder = taxonomySubjects.filter((item: any) => item.default).map((item) => item.id);
              this.defaultOrderOfFacets = activeFactsOrder.length
                    ? activeFactsOrder
                    : taxonomySubjects.filter(
                      (item, index) => (!this.hask12Enabled ? index <= 2
                        : (this.tenanSubject.indexOf(item.id) !== -1))).map((item) => item.id);
            }
            taxonomySubjects.forEach((subject: SubjectModel) => {
              subject.isActive = false;
            });
            const parsedSubjects =  (JSON.parse(JSON.stringify(taxonomySubjects)));
            // When default classification is not available
            if (defaultClassification.id === defaultClassificationId) {
              component.defaultOrderOfFacets.map((subjectCode: string) => {
                const matchingSubject = parsedSubjects.find( (subject: any) => subject.code === subjectCode);
                const taxonomySubject = taxonomySubjects.find((subject: SubjectModel) => subject.code === subjectCode);
                if (matchingSubject) {
                  component.selectedSubjects.push(matchingSubject);
                }

                if (taxonomySubject) {
                  taxonomySubject.isActive = true;
                }
              });
            } else {
              component.selectedSubjects = parsedSubjects;
            }
            resolve(component.selectedSubjects);
          });
          component.activeClassification = defaultClassification;
        });
      });
      Promise.all(promiseData).then(() => {
        this.fixedMathOrder();
        component.$emit('listActiveFacets', component.selectedSubjects);
      });
    });
  }

  private fixedMathOrder() {
    const component = this;
    const filterContentWithoutMath = component.selectedSubjects.filter(
            (item) =>  item.title.toLocaleLowerCase().indexOf('math') === -1);
    const filterMath = component.selectedSubjects.filter(
            (item) =>  item.title.toLocaleLowerCase().indexOf('math') !== -1);
    sortByProperty(filterContentWithoutMath, 'title', 'ASC');
    component.selectedSubjects = filterMath.concat(filterContentWithoutMath);
  }


  // -----------------------------------------------------------------
  // Actions

  private onSelectClassification(classification: ClassificationModel) {
    this.activeClassification = classification;
    this.loadTaxonomySubjects(classification.id);
  }

  private onSelectSubject(subject: SubjectModel) {
    const component = this;
    const selectedSubjects = component.selectedSubjects;
    const existingSubject: SubjectModel | any = selectedSubjects.find(
      (selectedSubject: SubjectModel) => subject.id === selectedSubject.id);
    subject.isActive = !subject.isActive;
    const selectedSubjectIndex =  selectedSubjects.indexOf(existingSubject);
    if (selectedSubjectIndex > -1) {
      selectedSubjects.splice(selectedSubjectIndex, 1);
    } else {
      selectedSubjects.push(subject);
    }
    component.selectedSubjects = selectedSubjects;
  }

  private onApplyFilters() {
    this.fixedMathOrder();
    this.$emit('listActiveFacets', this.selectedSubjects);
    this.isShowFilterBody = false;
  }

  private loadTaxonomySubjects(classificationCode: string) {
    const component = this;
    return taxonomyAPI.fetchTaxonomySubjects(classificationCode).then((taxonomySubjects: SubjectModel[]) => {
      component.subjects = taxonomySubjects;
      const selectedSubjects = component.selectedSubjects;
      selectedSubjects.map( (selectedSubject: SubjectModel) => {
        const existingSubject = taxonomySubjects.find((subject: SubjectModel) => selectedSubject.code === subject.code);
        if (existingSubject) {
          existingSubject.isActive = true;
        }
      });
      return taxonomySubjects;
    });
  }

}
